import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * Unified scanner-access editor — drives BOTH `custom:customers` and
 * `custom:scanners` in a single dialog. Mike 2026-06-28: the two
 * attributes are co-dependent — every entry in custom:scanners must
 * belong to a customer in custom:customers — so editing them via two
 * separate modals lets them drift. One editor, one save.
 *
 *   <app-access-editor
 *     *ngIf="showEditUserAccessModal"
 *     [customers]="directoryCustomers"
 *     [scanners]="scanners"
 *     [userCustomers]="user.attributes['custom:customers']"
 *     [userScanners]="user.attributes['custom:scanners']"
 *     [userPoolId]="user.pool_id"
 *     [userOwnSlug]="user.customer_slug"
 *     [userDisplayName]="userName"
 *     [userEmail]="userEmail"
 *     (save)="onSaveAccess($event)"
 *     (close)="showEditUserAccessModal = false"
 *   ></app-access-editor>
 *
 * Layout:
 *   - One card per POOL-COMPATIBLE customer (user-in-legacy-pool sees
 *     legacy customers; user-in-customer-pool sees only that customer).
 *     Customers on a different pool are EXCLUDED entirely — the user
 *     can't be granted access there without re-provisioning in that
 *     pool, so showing them as disabled would mislead.
 *   - Card header has an enable checkbox that drives custom:customers
 *     membership for that slug. Header click toggles. Disabled card =
 *     collapsed, no scanner picks visible.
 *   - Card body (when enabled) shows one checkbox per scanner the
 *     customer owns, plus a top "Include all (wildcard)" toggle that
 *     swaps the saved value to the customer's wildcard token (e.g.
 *     "tt-*"). When wildcard is on, individual scanner checkboxes are
 *     disabled + visually checked.
 *
 * Save shape (emitted as one object):
 *   {
 *     customers: "alpha-shark,hima-reddy",
 *     scanners:  "as-os,as-dash,tt-*"
 *   }
 *
 * The user's own customer slug (`userOwnSlug`) is forced enabled and
 * its checkbox is locked — same defensive rule the legacy customers
 * modal uses (a per-customer-pool user that's missing their own slug
 * would otherwise be deletable in this UI). Scanner-level picks
 * within that card are still admin-controlled.
 */

interface CustomerLite {
  _id: string;
  name?: string;
  scanner_ids?: string[];
  cognito_pool?: { user_pool_id?: string };
}

interface ScannerLite {
  _id?: string;
  Id?: string;
  Name?: string;
  Client?: string;
}

interface AccessCard {
  customer: CustomerLite;
  scanners: ScannerLite[];          // scanners this customer owns
  wildcard: string;                  // e.g. "as-*" — computed from common prefix
  enabled: boolean;                  // slug in custom:customers
  expanded: boolean;                  // UX only; mirrors enabled by default
  wildcardChecked: boolean;          // when true, save uses the wildcard token instead of per-scanner ids
  selectedScannerIds: Set<string>;   // individual scanner picks (ignored when wildcardChecked)
  isOwnPool: boolean;                 // true when this is the user's own per-customer-pool customer
}

@Component({
  standalone: true,
  selector: 'app-access-editor',
  imports: [CommonModule, FormsModule],
  templateUrl: './access-editor.component.html',
})
export class AccessEditorComponent implements OnChanges {
  @Input() customers: CustomerLite[] = [];
  @Input() scanners: ScannerLite[] = [];
  @Input() userCustomers: string = '';
  @Input() userScanners: string = '';
  // Cognito pool the user lives in. '' or undefined = legacy.
  @Input() userPoolId: string = '';
  // Target user's custom:role — when 'admin', the pool-compatibility
  // filter is bypassed and every customer + scanner in the catalog is
  // pickable. Admins span every customer by convention (documented in
  // memory feedback_admin_customers_scanners_unfiltered_by_pool) and
  // living in the legacy pool doesn't mean they only touch legacy
  // customers. Mike 2026-07-06.
  @Input() userRole: string = '';
  // The user's "own" customer slug (the customer whose pool they live
  // in). Locked enabled. Skip the lock for legacy users (their own slug
  // is conventionally not stamped on the row).
  @Input() userOwnSlug: string = '';
  @Input() title: string = 'Edit Access';
  @Input() userDisplayName: string = '';
  @Input() userEmail: string = '';
  // Parent-driven save-in-flight flag. When true the modal locks down
  // (Save shows spinner + disabled, Cancel disabled, click-outside
  // ignored) so the operator can SEE the write is happening — without
  // this the modal goes silent for 1-5s during the Cognito write +
  // Redis re-cache and operators think "nothing happened" and click
  // away. Parent flips this true around its await.
  @Input() saving: boolean = false;

  @Output() save = new EventEmitter<{ customers: string; scanners: string }>();
  @Output() close = new EventEmitter<void>();

  cards: AccessCard[] = [];
  // Scanner tokens (literals or wildcards) in custom:scanners that
  // don't map to any visible card — preserved on save so this dialog
  // never silently strips them.
  orphanScannerTokens: string[] = [];

  ngOnChanges(_: SimpleChanges) {
    this._build_cards();
  }

  private _normalize_pool(p: string | undefined | null): string {
    return String(p || '').trim();
  }

  private _isLegacyUser(): boolean {
    const p = this._normalize_pool(this.userPoolId);
    // Heuristic: legacy pool ids end with '0Q00rhHoS' historically, but
    // we treat ANY user whose pool doesn't match a per-customer pool as
    // "legacy" for visibility purposes. Walk the directory; if user's
    // pool matches any customer's cognito_pool.user_pool_id → per-
    // customer; otherwise legacy.
    if (!p) return true;
    for (const c of (this.customers || [])) {
      if (this._normalize_pool(c.cognito_pool?.user_pool_id) === p) return false;
    }
    return true;
  }

  private _isAdminTarget(): boolean {
    return String(this.userRole || '').trim().toLowerCase() === 'admin';
  }

  private _customerPoolMatches(c: CustomerLite): boolean {
    // Admin-role targets: every customer is pickable regardless of pool.
    // Admins span the whole fleet — the legacy-only visibility rule
    // designed for member/client-role users doesn't apply.
    if (this._isAdminTarget()) return true;
    const userP = this._normalize_pool(this.userPoolId);
    const custP = this._normalize_pool(c.cognito_pool?.user_pool_id);
    if (this._isLegacyUser()) return !custP;     // legacy user → legacy customers only (no per-customer pool)
    return custP === userP;                        // per-customer-pool user → only their own customer
  }

  private _scannersForCustomer(slug: string): ScannerLite[] {
    return (this.scanners || []).filter(s => String(s.Client || '') === slug);
  }

  private _longestCommonPrefix(ids: string[]): string {
    if (!ids.length) return '';
    let p = ids[0];
    for (let i = 1; i < ids.length; i++) {
      let j = 0;
      while (j < p.length && j < ids[i].length && p[j] === ids[i][j]) j++;
      p = p.slice(0, j);
      if (!p) break;
    }
    return p;
  }

  private _build_cards() {
    this.cards = [];
    this.orphanScannerTokens = [];

    const entitledCustomers = new Set(
      String(this.userCustomers || '').split(',').map(s => s.trim()).filter(Boolean),
    );
    const scannerTokens = String(this.userScanners || '').split(',').map(s => s.trim()).filter(Boolean);
    const wildcardTokens = new Set(scannerTokens.filter(t => t.endsWith('*')));
    const literalTokens = new Set(scannerTokens.filter(t => !t.endsWith('*')));

    const ownSlug = String(this.userOwnSlug || '').trim().toLowerCase();
    const matchedTokens = new Set<string>();

    for (const c of (this.customers || [])) {
      if (!c?._id) continue;
      if (!this._customerPoolMatches(c)) continue;
      const scs = this._scannersForCustomer(c._id);
      if (!scs.length) continue;
      const ids = scs.map(s => String(s._id || s.Id || ''));
      const prefix = this._longestCommonPrefix(ids);
      const wildcard = prefix ? `${prefix}*` : `${c._id}-*`;
      const wildcardOn = wildcardTokens.has(wildcard);
      const selected = new Set<string>();
      if (!wildcardOn) {
        for (const id of ids) if (literalTokens.has(id)) selected.add(id);
      }
      if (wildcardOn) matchedTokens.add(wildcard);
      for (const id of ids) if (literalTokens.has(id)) matchedTokens.add(id);

      const isOwn = !!ownSlug && ownSlug === String(c._id).toLowerCase();
      const enabled = entitledCustomers.has(c._id) || isOwn;
      // All cards start collapsed (Mike 2026-06-28). Collapsed
      // enabled cards still show their preview summary so the admin
      // gets the at-a-glance picture without N pre-expanded grids.
      this.cards.push({
        customer: c,
        scanners: scs,
        wildcard,
        enabled,
        expanded: false,
        wildcardChecked: wildcardOn,
        selectedScannerIds: selected,
        isOwnPool: isOwn,
      });
    }

    this.cards.sort((a, b) => {
      // Own-pool card first; then alpha by customer name.
      if (a.isOwnPool !== b.isOwnPool) return a.isOwnPool ? -1 : 1;
      const an = String(a.customer.name || a.customer._id).toLowerCase();
      const bn = String(b.customer.name || b.customer._id).toLowerCase();
      return an.localeCompare(bn);
    });

    // Scanner tokens that didn't map to any visible card → orphans
    // (preserved on save so we never silently strip data).
    for (const t of scannerTokens) {
      if (!matchedTokens.has(t)) this.orphanScannerTokens.push(t);
    }
  }

  toggleCardEnabled(card: AccessCard, value: boolean) {
    if (card.isOwnPool) return;            // locked enabled
    card.enabled = value;
    if (value) {
      card.expanded = true;
    } else {
      card.expanded = false;
      // Clear scanner picks too — disabling a customer drops every
      // scanner under it from custom:scanners on save.
      card.wildcardChecked = false;
      card.selectedScannerIds.clear();
    }
  }

  onHeaderToggle(card: AccessCard) {
    // Header click only expands/collapses — never touches `enabled`.
    // The enable checkbox in the header is the sole control for
    // adding/removing the customer slug from custom:customers; that
    // way an admin can peek inside a disabled card without
    // accidentally enabling it, and can collapse an enabled card
    // without dropping it from custom:customers.
    card.expanded = !card.expanded;
  }

  toggleWildcard(card: AccessCard, value: boolean) {
    card.wildcardChecked = value;
    if (value) {
      // Wildcard absorbs the per-scanner picks — clear them so save
      // emits a single token instead of a literal+wildcard duplicate.
      card.selectedScannerIds.clear();
    }
  }

  toggleScanner(card: AccessCard, scannerId: string) {
    if (card.wildcardChecked) return;       // disabled when wildcard is on
    if (card.selectedScannerIds.has(scannerId)) card.selectedScannerIds.delete(scannerId);
    else card.selectedScannerIds.add(scannerId);
  }

  isScannerChecked(card: AccessCard, scannerId: string): boolean {
    if (card.wildcardChecked) return true;
    return card.selectedScannerIds.has(scannerId);
  }

  isScannerDisabled(card: AccessCard): boolean {
    return card.wildcardChecked;
  }

  selectAll(card: AccessCard) {
    card.wildcardChecked = false;
    card.selectedScannerIds.clear();
    for (const s of card.scanners) {
      const id = String(s._id || s.Id || '');
      if (id) card.selectedScannerIds.add(id);
    }
  }
  clearAll(card: AccessCard) {
    card.wildcardChecked = false;
    card.selectedScannerIds.clear();
  }

  cardSummary(card: AccessCard): string {
    if (!card.enabled) return '';
    if (card.wildcardChecked) return `all (${card.wildcard})`;
    const total = card.scanners.length;
    const sel = card.selectedScannerIds.size;
    if (sel === 0) return `0 / ${total}`;
    // Preview the first few selected ids (in scanner.declaration order
    // so the slugs read deterministically), then a "+N more" tail when
    // truncated. Useful when collapsed since the body grid isn't
    // visible.
    const PREVIEW = 3;
    const ordered: string[] = [];
    for (const s of card.scanners) {
      const id = String(s._id || s.Id || '');
      if (id && card.selectedScannerIds.has(id)) ordered.push(id);
    }
    const head = ordered.slice(0, PREVIEW).join(', ');
    const more = ordered.length > PREVIEW ? ` +${ordered.length - PREVIEW}` : '';
    return `${head}${more} (${sel} / ${total})`;
  }

  removeOrphan(token: string) {
    // Orphan tokens are "scanner tokens" by section name, but a token can
    // ALSO be a customer slug (e.g. `delphi-sig` is both Customer._id AND
    // Scanner._id with Client='delphi-sig'). When the operator removes a
    // token that matches a customer slug, sweep every scanner owned by
    // that customer (F2-ADMIN.Scanners where Client===token) out of the
    // orphan list too — otherwise they'd have to click × on each scanner
    // pill separately, which is tedious and easy to miss.
    const toRemove = new Set<string>([token]);
    for (const s of (this.scanners || [])) {
      if (String(s.Client || '') === token) {
        const id = String(s._id || s.Id || '');
        if (id) toRemove.add(id);
      }
    }
    this.orphanScannerTokens = this.orphanScannerTokens.filter(t => !toRemove.has(t));
  }

  /**
   * Friendly pool name for the user being edited — shown in the modal
   * header so the operator sees which Cognito pool's identity they're
   * editing (the same email can exist as a separate identity in legacy
   * AND a per-customer pool, with different attributes per pool). When
   * userPoolId matches a customer's cognito_pool, use that customer's
   * display name; otherwise the user is on legacy.
   */
  get userPoolName(): string {
    const p = this._normalize_pool(this.userPoolId);
    if (!p) return 'F2 (Legacy)';
    for (const c of (this.customers || [])) {
      if (this._normalize_pool(c.cognito_pool?.user_pool_id) === p) {
        return c.name || c._id;
      }
    }
    return 'F2 (Legacy)';
  }

  expandAll() {
    for (const c of this.cards) c.expanded = true;
  }
  collapseAll() {
    for (const c of this.cards) c.expanded = false;
  }

  onSave() {
    const customers: string[] = [];
    const scanners: string[] = [];
    const seenCust = new Set<string>();
    const seenScan = new Set<string>();
    const pushC = (s: string) => { if (s && !seenCust.has(s)) { seenCust.add(s); customers.push(s); } };
    const pushS = (s: string) => { if (s && !seenScan.has(s)) { seenScan.add(s); scanners.push(s); } };

    for (const card of this.cards) {
      if (!card.enabled) continue;
      pushC(card.customer._id);
      if (card.wildcardChecked) {
        pushS(card.wildcard);
      } else {
        for (const s of card.scanners) {
          const id = String(s._id || s.Id || '');
          if (card.selectedScannerIds.has(id)) pushS(id);
        }
      }
    }
    // Preserve orphan scanner tokens (e.g. wildcards or literals for
    // customers excluded by pool gating) so the editor doesn't silently
    // drop them.
    for (const t of this.orphanScannerTokens) pushS(t);

    this.save.emit({
      customers: customers.join(','),
      scanners: scanners.join(','),
    });
  }

  onCancel() {
    if (this.saving) return;     // mid-save → don't let click-outside or Cancel dismiss
    this.close.emit();
  }
}
